import { HubConnectionBuilder } from '@microsoft/signalr';
import type { HubConnection, IRetryPolicy } from '@microsoft/signalr';
import type { PortalHubClientEvents, PortalHubServerMethods } from './contracts/portalHub';

export type HubEventMap = Record<string, (...args: never[]) => void>;
export type HubMethodMap = Record<string, (...args: never[]) => Promise<unknown>>;

export type NoServerMethods = Record<never, never>;

export type TypedHubConnection<TEvents extends HubEventMap, TMethods extends HubMethodMap = NoServerMethods> = {
  readonly connection: HubConnection;
  readonly hubName: string;
  start(): Promise<void>;
  stop(): Promise<void>;
  on<TEventName extends keyof TEvents & string>(eventName: TEventName, handler: TEvents[TEventName]): () => void;
  invoke<TMethodName extends keyof TMethods & string>(
    methodName: TMethodName,
    ...args: Parameters<TMethods[TMethodName]>
  ): ReturnType<TMethods[TMethodName]>;
  onReconnecting(callback: (error?: Error) => void): () => void;
  onReconnected(callback: (connectionId?: string) => void): () => void;
  onClose(callback: (error?: Error) => void): () => void;
  dispose(): Promise<void>;
};

export type CreateTypedHubConnectionOptions = {
  url: string;
  hubName: string;
  accessTokenFactory?: () => Promise<string>;
};

let logsIncidentAndMedicalPayloadsToConsole = false;

export function setRealtimeDebug(enabled: boolean): void {
  logsIncidentAndMedicalPayloadsToConsole = enabled;
}

function logEvent(hubName: string, methodName: string, args: unknown[]): void {
  if (!logsIncidentAndMedicalPayloadsToConsole) {
    return;
  }
  const data = args.length === 0 ? null : JSON.stringify(args.length === 1 ? args[0] : args);
  if (data !== null) {
    console.log(`[SignalR] ${hubName} <- ${methodName}: ${data}`);
  } else {
    console.log(`[SignalR] ${hubName} <- ${methodName}`);
  }
}

const RECONNECT_DELAYS_MS = [0, 2_000, 5_000, 10_000] as const;
const MAX_STALE_PAGE_WINDOW_MS = 15_000;

export function nextReconnectDelayMs(previousRetryCount: number): number {
  return RECONNECT_DELAYS_MS[previousRetryCount] ?? MAX_STALE_PAGE_WINDOW_MS;
}

const retryForAsLongAsThePageIsOpen: IRetryPolicy = {
  nextRetryDelayInMilliseconds: (retryContext) => nextReconnectDelayMs(retryContext.previousRetryCount),
};

export function createTypedHubConnection<TEvents extends HubEventMap, TMethods extends HubMethodMap = NoServerMethods>(
  options: CreateTypedHubConnectionOptions,
): TypedHubConnection<TEvents, TMethods> {
  const connection = new HubConnectionBuilder()
    .withUrl(options.url, {
      accessTokenFactory: options.accessTokenFactory,
    })
    .withAutomaticReconnect(retryForAsLongAsThePageIsOpen)
    .build();

  const reconnectingCallbacks = new Set<(error?: Error) => void>();
  const reconnectedCallbacks = new Set<(connectionId?: string) => void>();
  const closeCallbacks = new Set<(error?: Error) => void>();

  connection.onreconnecting((error) => reconnectingCallbacks.forEach((callback) => callback(error)));
  connection.onreconnected((connectionId) => reconnectedCallbacks.forEach((callback) => callback(connectionId)));
  connection.onclose((error) => closeCallbacks.forEach((callback) => callback(error)));

  return {
    connection,
    hubName: options.hubName,
    start: () => connection.start(),
    stop: () => connection.stop(),
    on(eventName, handler) {
      const wrappedHandler = (...args: unknown[]): void => {
        logEvent(options.hubName, eventName, args);
        (handler as unknown as (...handlerArgs: unknown[]) => void)(...args);
      };
      connection.on(eventName, wrappedHandler);
      return () => connection.off(eventName, wrappedHandler);
    },
    invoke(methodName, ...args) {
      type Result = ReturnType<TMethods[typeof methodName]>;
      return connection.invoke(methodName, ...args) as Result;
    },
    onReconnecting(callback) {
      reconnectingCallbacks.add(callback);
      return () => reconnectingCallbacks.delete(callback);
    },
    onReconnected(callback) {
      reconnectedCallbacks.add(callback);
      return () => reconnectedCallbacks.delete(callback);
    },
    onClose(callback) {
      closeCallbacks.add(callback);
      return () => closeCallbacks.delete(callback);
    },
    dispose: () => connection.stop(),
  };
}

export type PortalHubConnection = TypedHubConnection<PortalHubClientEvents, PortalHubServerMethods>;

export type HubFactoryOptions = {
  emsServerUrl: string;
  accessTokenFactory: () => Promise<string>;
};

export type HubFactory = {
  createPortalHubConnection(): PortalHubConnection;
};

export function createHubFactory(options: HubFactoryOptions): HubFactory {
  const emsServerUrl = options.emsServerUrl.replace(/\/+$/, '');
  const { accessTokenFactory } = options;

  return {
    createPortalHubConnection() {
      return createTypedHubConnection({
        url: `${emsServerUrl}/hub/portal`,
        hubName: 'PortalHubConnection',
        accessTokenFactory,
      });
    },
  };
}
